import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { NativeSelect, Spinner } from "@/components/Common";
import { api, asList } from "@/lib/api";

const JobSearch = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (q.trim().length < 2) { setRes([]); return; }
    t.current = setTimeout(async () => {
      setBusy(true);
      try { setRes(asList((await api.get("/jobs/search", { params: { query: q } })).data, "jobs").slice(0, 6)); } catch { setRes([]); } finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t.current);
  }, [q]);
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="...or search any job" className="bg-[#09090B] pl-8 text-sm" data-testid="job-picker-search" />
      {busy && <Spinner className="absolute right-3 top-3 text-[#D4AF37]" />}
      {res.length > 0 && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-[#27272A] bg-[#18181B] shadow-xl" data-testid="job-picker-results">
          {res.map((j) => (
            <button key={j.id} type="button" onClick={() => { onPick(j); setQ(""); setRes([]); }} data-testid={`job-picker-result-${j.id}`}
              className="block w-full truncate px-3 py-2 text-left text-sm text-zinc-300 transition-colors hover:bg-[#D4AF37]/10 hover:text-[#F3C649]">
              {j.title} <span className="text-zinc-500">· {j.company}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const JobPicker = ({ value, onChange, optional, autoSelect, initialJob }) => {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    const withInitial = (list) => (initialJob && !list.some((j) => j.id === initialJob.id) ? [initialJob, ...list] : list);
    api.get("/applications")
      .then((r) => setJobs(withInitial(asList(r.data, "applications").map((a) => ({ id: a.job_id || a.job?.id || a.id, title: a.job?.title || a.title || a.job_id, company: a.job?.company || a.company || "" })).filter((j) => j.id))))
      .catch(() => setJobs(withInitial([])));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoSelect && !value && jobs?.length) onChange(jobs[0].id); }, [autoSelect, jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (j) => { setJobs((js) => (js.some((x) => x.id === j.id) ? js : [{ id: j.id, title: j.title, company: j.company }, ...js])); onChange(j.id); };
  const options = [{ value: "", label: optional ? "No specific job" : jobs?.length ? "Choose a job" : "No saved jobs yet, search below" },
    ...(jobs || []).map((j) => ({ value: j.id, label: `${j.title}${j.company ? ` · ${j.company}` : ""}` }))];
  return (
    <div className="space-y-2">
      <NativeSelect value={value} onChange={onChange} options={options} className="w-full" data-testid="job-picker" />
      <JobSearch onPick={pick} />
    </div>
  );
};
